import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query'
import { useId, useState } from 'react'
import { useApp } from '@/app/AppContext'
import { Icon } from '@/components/Icon'
import { ConfirmModal, StateView, type MenuAction } from '@/card/record'
import { columnLabel } from '@/lists/columnLabel'
import { TAGGABLE, deleteSegment, deleteTag, fetchSegments, fetchTags, updateTag, type Segment, type Tag } from '@/lists/tagsApi'
import type { FilterSpec } from '@/lists/types'
import {
  AdminPage,
  AdminState,
  EditLayer,
  Field,
  LayerButtons,
  Notice,
  PrimaryButton,
  RowMenu,
  SearchBox,
  SkeletonTable,
  SwatchPick,
  TagChip,
  Tokens,
  colorName,
  isForbidden,
  useAdminEditing,
} from './AdminShell'
import { createTag, updateSegmentSharing } from './api'
import { resourceLabel } from './labels'

/**
 * Теги и сегменты (тикет #105, макет AdmTags.dc.html).
 *
 * Тег — ручная метка; переименование, цвет и удаление — право администратора, потому что
 * меняет смысл каждого сегмента, построенного по тегу: удалённый тег снимается со всех
 * записей разом, и об этом спрашивают до удаления.
 *
 * Сегмент — сохранённый фильтр списка; его число записей считается в области видимости
 * смотрящего, поэтому у руководителя и менеджера оно разное. Заводится сегмент в списке
 * («Сохранить как сегмент») — фильтр там собирается по настоящим колонкам; здесь общий
 * доступ и удаление. Чего справочник тегов не хранит — к каким сущностям тег применим,
 * сколько записей им помечено, кто его завёл, — на экране нет.
 */
type Tab = 'tags' | 'segments'

export function TagsPage() {
  const { t } = useApp()
  const editing = useAdminEditing()
  const queryClient = useQueryClient()
  const [tab, setTab] = useState<Tab>('tags')
  const [query, setQuery] = useState('')
  const [entity, setEntity] = useState('all')
  const [sharedOnly, setSharedOnly] = useState(false)
  const [tagDraft, setTagDraft] = useState<{ id: string | null; name: string; color: string } | null>(null)
  const [removingTag, setRemovingTag] = useState<Tag | null>(null)
  const [removingSegment, setRemovingSegment] = useState<Segment | null>(null)

  const tags = useQuery({ queryKey: ['tags'], queryFn: ({ signal }) => fetchTags(signal) })
  const entities = [...TAGGABLE]
  const segmentQueries = useQueries({
    queries: entities.map((name) => ({
      queryKey: ['segments', name],
      queryFn: ({ signal }: { signal: AbortSignal }) => fetchSegments(name, signal),
      enabled: tab === 'segments',
    })),
  })

  const refreshTags = () => {
    editing.clear()
    void queryClient.invalidateQueries({ queryKey: ['tags'] })
  }
  const refreshSegments = () => {
    editing.clear()
    void queryClient.invalidateQueries({ queryKey: ['segments'] })
  }
  const removeTag = useMutation({
    mutationFn: deleteTag,
    onSuccess: () => {
      setRemovingTag(null)
      refreshTags()
    },
    onError: (error) => {
      setRemovingTag(null)
      editing.fail(error)
    },
  })
  const share = useMutation({
    mutationFn: ({ id, shared }: { id: string; shared: boolean }) => updateSegmentSharing(id, shared),
    onSuccess: refreshSegments,
    onError: editing.fail,
  })
  const removeSegment = useMutation({
    mutationFn: deleteSegment,
    onSuccess: () => {
      setRemovingSegment(null)
      refreshSegments()
    },
    onError: (error) => {
      setRemovingSegment(null)
      editing.fail(error)
    },
  })

  const needle = query.trim().toLowerCase()
  const tagList = (tags.data ?? []).filter((tag) => !needle || tag.name.toLowerCase().includes(needle))
  const segments = segmentQueries.flatMap((result) => result.data ?? [])
  const segmentList = segments
    .filter((segment) => entity === 'all' || segment.entity === entity)
    .filter((segment) => !sharedOnly || segment.shared)
    .filter((segment) => !needle || segment.name.toLowerCase().includes(needle))
  const segmentsLoading = segmentQueries.some((result) => result.isLoading)
  const segmentsError = segmentQueries.find((result) => result.error)?.error ?? null
  const forbidden = isForbidden(tags.error)

  return (
    <AdminPage
      titleKey="adm.tags.title"
      leadKey="adm.tags.lead"
      readOnly={!editing.mayEdit}
      roKey="adm.tags.ro"
      forbidden={forbidden}
      primary={editing.mayEdit && tab === 'tags' && (
        <PrimaryButton label={t('adm.tags.add')} onClick={() => setTagDraft({ id: null, name: '', color: '' })} />
      )}
    >
      <Notice text={editing.notice} />
      {!forbidden && (
        <>
          <div className="tabs" role="tablist" aria-label={t('adm.tags.title')}>
            <button type="button" role="tab" className="tab" aria-selected={tab === 'tags'} onClick={() => setTab('tags')}>
              {t('adm.tags.tab.tags')} <span className="count-pill">{tags.data?.length ?? 0}</span>
            </button>
            <button type="button" role="tab" className="tab" aria-selected={tab === 'segments'} onClick={() => setTab('segments')}>
              {t('adm.tags.tab.segments')}
              {tab === 'segments' && !segmentsLoading && <span className="count-pill">{segments.length}</span>}
            </button>
          </div>
          <div className="toolbar">
            <SearchBox value={query} onChange={setQuery} label={t(tab === 'tags' ? 'adm.tags.search' : 'adm.segments.search')} />
            {tab === 'segments' && (
              <>
                <div className="vsep" />
                <span className="t-sm muted">{t('adm.segments.entity')}</span>
                <div className="seg" role="group" aria-label={t('adm.segments.entity')}>
                  <button type="button" aria-pressed={entity === 'all'} onClick={() => setEntity('all')}>{t('adm.segments.all')}</button>
                  {entities.map((name) => (
                    <button key={name} type="button" aria-pressed={entity === name} onClick={() => setEntity(name)}>
                      {resourceLabel(t, name)}
                    </button>
                  ))}
                </div>
                <div className="vsep" />
                <button type="button" className="chip" aria-pressed={sharedOnly} onClick={() => setSharedOnly(!sharedOnly)}>
                  {t('adm.segments.sharedOnly')} <span className="count">{segments.filter((segment) => segment.shared).length}</span>
                </button>
              </>
            )}
          </div>
        </>
      )}

      {tab === 'tags' ? (
        <AdminState isLoading={tags.isLoading} error={tags.error} onRetry={() => void tags.refetch()}
                    forbiddenTitleKey="adm.tags.forbidden" errorTitleKey="adm.tags.error"
                    skeleton={<SkeletonTable columns={3} rows={8} />}>
          {(tags.data ?? []).length === 0 ? (
            <StateView boxed kind="empty" title={t('adm.tags.empty')} description={t('adm.tags.empty.text')}
                       actions={editing.mayEdit && (
                         <button type="button" className="btn btn-secondary btn-sm" onClick={() => setTagDraft({ id: null, name: '', color: '' })}>
                           {t('adm.tags.add')}
                         </button>
                       )} />
          ) : tagList.length === 0 ? (
            <StateView boxed kind="empty" title={t('adm.tags.notFound', { query })}
                       actions={<button type="button" className="btn btn-secondary btn-sm" onClick={() => setQuery('')}>{t('adm.resetSearch')}</button>} />
          ) : (
            <>
              <div className="tbl-wrap">
                <table className="tbl" aria-label={t('nav.adminTags')}>
                  <thead>
                    <tr>
                      <th>{t('admin.tags.name')}</th>
                      <th>{t('admin.tags.color')}</th>
                      {editing.mayEdit && <th className="adm-act"><span className="sr-only">{t('adm.actions')}</span></th>}
                    </tr>
                  </thead>
                  <tbody>
                    {[...tagList].sort((a, b) => a.name.localeCompare(b.name)).map((tag) => (
                      <tr key={tag.id} className={tagDraft?.id === tag.id ? 'is-selected' : undefined}>
                        <td><TagChip name={tag.name} color={tag.color} /></td>
                        <td className="secondary">{colorName(t, tag.color)}</td>
                        {editing.mayEdit && (
                          <td className="adm-act">
                            <RowMenu label={t('adm.tags.actions', { name: tag.name })} items={[
                              { id: 'edit', label: t('admin.edit'), icon: 'adm-edit', onSelect: () => setTagDraft({ id: tag.id, name: tag.name, color: tag.color ?? '' }) },
                              { id: 'delete', label: t('admin.tags.delete'), icon: 'trash', danger: true, onSelect: () => setRemovingTag(tag) },
                            ]} />
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="t-sm muted">{t('adm.tags.note')}</p>
            </>
          )}
        </AdminState>
      ) : (
        <AdminState isLoading={segmentsLoading} error={segmentsError} onRetry={() => segmentQueries.forEach((result) => void result.refetch())}
                    errorTitleKey="adm.segments.error" skeleton={<SkeletonTable columns={5} rows={8} />}>
          {segments.length === 0 ? (
            <StateView boxed kind="empty" title={t('adm.segments.empty')} description={t('adm.segments.empty.text')} />
          ) : segmentList.length === 0 ? (
            <StateView boxed kind="empty" title={t('adm.segments.notFound')}
                       actions={<button type="button" className="btn btn-secondary btn-sm" onClick={() => {
                         setQuery('')
                         setEntity('all')
                         setSharedOnly(false)
                       }}>{t('adm.resetFilters')}</button>} />
          ) : (
            <>
              <div className="tbl-wrap">
                <table className="tbl" aria-label={t('adm.tags.tab.segments')}>
                  <thead>
                    <tr>
                      <th>{t('adm.segments.name')}</th>
                      <th>{t('adm.segments.entity')}</th>
                      <th>{t('adm.segments.filter')}</th>
                      <th className="num">{t('adm.segments.count')}</th>
                      <th>{t('adm.segments.access')}</th>
                      <th className="adm-act"><span className="sr-only">{t('adm.actions')}</span></th>
                    </tr>
                  </thead>
                  <tbody>
                    {segmentList.map((segment) => {
                      const items: MenuAction[] = []
                      if (editing.mayEdit) {
                        items.push(segment.shared
                          ? { id: 'private', label: t('adm.segments.makePrivate'), icon: 'lock', onSelect: () => share.mutate({ id: segment.id, shared: false }) }
                          : { id: 'shared', label: t('adm.segments.makeShared'), icon: 'adm-users', onSelect: () => share.mutate({ id: segment.id, shared: true }) })
                      }
                      if (editing.mayEdit || segment.mine) {
                        items.push({ id: 'delete', label: t('adm.segments.delete'), icon: 'trash', danger: true, onSelect: () => setRemovingSegment(segment) })
                      }
                      return (
                        <tr key={segment.id}>
                          <td><span className="strong">{segment.name}</span></td>
                          <td>{resourceLabel(t, segment.entity)}</td>
                          <td className="wrap">
                            {segment.filters.length === 0 ? (
                              <span className="muted">{t('adm.segments.noFilter')}</span>
                            ) : (
                              <Tokens join={t('adm.rule.and')} items={segment.filters.map((filter) => ({
                                k: columnLabel(t, undefined, filter.column),
                                op: t(`filter.op.${filter.operator}`),
                                v: filterValue(filter),
                              }))} />
                            )}
                          </td>
                          <td className="num">{segment.count}</td>
                          <td>
                            {segment.shared ? (
                              <span className="badge b-info"><Icon name="adm-users" />{t('adm.segments.shared')}</span>
                            ) : (
                              <span className="badge"><Icon name="lock" />{t(segment.mine ? 'adm.segments.mine' : 'adm.segments.private')}</span>
                            )}
                          </td>
                          <td className="adm-act">
                            {items.length > 0 && <RowMenu label={t('adm.segments.actions', { name: segment.name })} items={items} />}
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
              <p className="t-sm muted">{t('adm.segments.note')}</p>
            </>
          )}
        </AdminState>
      )}

      {tagDraft && (
        <TagModal draft={tagDraft} onChange={setTagDraft} onClose={() => setTagDraft(null)} onSaved={() => {
          setTagDraft(null)
          refreshTags()
        }} />
      )}
      <ConfirmModal
        open={removingTag != null}
        title={t('adm.tags.deleteConfirm', { name: removingTag?.name ?? '' })}
        confirmLabel={t('admin.tags.delete')}
        pending={removeTag.isPending}
        onCancel={() => setRemovingTag(null)}
        onConfirm={() => removingTag && removeTag.mutate(removingTag.id)}
      >
        <p>{t('adm.tags.deleteConfirm.text')}</p>
      </ConfirmModal>
      <ConfirmModal
        open={removingSegment != null}
        title={t('adm.segments.deleteConfirm', { name: removingSegment?.name ?? '' })}
        confirmLabel={t('adm.segments.delete')}
        pending={removeSegment.isPending}
        onCancel={() => setRemovingSegment(null)}
        onConfirm={() => removingSegment && removeSegment.mutate(removingSegment.id)}
      >
        <p>{t('adm.segments.deleteConfirm.text')}</p>
      </ConfirmModal>
    </AdminPage>
  )
}

function filterValue(filter: FilterSpec): string {
  const value = filter.value
  if (value == null || value === undefined) return ''
  if (Array.isArray(value)) return value.map(String).join(', ')
  if (typeof value === 'object') return JSON.stringify(value)
  return String(value)
}

function TagModal({ draft, onChange, onClose, onSaved }: {
  draft: { id: string | null; name: string; color: string }
  onChange: (draft: { id: string | null; name: string; color: string }) => void
  onClose: () => void
  onSaved: () => void
}) {
  const { t } = useApp()
  const formId = useId()
  const colorId = useId()
  const [error, setError] = useState<string | null>(null)
  const save = useMutation({
    mutationFn: () =>
      draft.id
        ? updateTag(draft.id, { name: draft.name, color: draft.color })
        : createTag({ name: draft.name.trim(), color: draft.color || null }),
    onSuccess: onSaved,
    onError: (failure) => setError(failure instanceof Error ? failure.message : t('app.error')),
  })
  return (
    <EditLayer
      kind="modal"
      title={draft.id ? t('adm.tags.edit') : t('adm.tags.new')}
      subtitle={draft.id ? undefined : t('adm.tags.new.sub')}
      onClose={onClose}
      footer={<LayerButtons onCancel={onClose} saveLabel={draft.id ? t('admin.save') : t('adm.tags.create')}
                            pending={save.isPending} disabled={draft.name.trim() === ''} form={formId} />}
    >
      <form id={formId} className="stack" style={{ gap: 16 }} aria-label={t('adm.tags.form')} onSubmit={(event) => {
        event.preventDefault()
        setError(null)
        save.mutate()
      }}>
        <Field label={t('admin.tags.name')} htmlFor="tg-name" required help={draft.id ? t('adm.tags.renameHelp') : undefined}>
          <input id="tg-name" className="input" maxLength={60} value={draft.name} onChange={(e) => onChange({ ...draft, name: e.target.value })} />
        </Field>
        <div className="field">
          <span className="label" id={colorId}>{t('admin.tags.color')}</span>
          <SwatchPick value={draft.color} labelledBy={colorId} onChange={(color) => onChange({ ...draft, color })} />
        </div>
        <div className="field">
          <span className="label">{t('adm.tags.preview')}</span>
          <div className="row" style={{ gap: 8, padding: '10px 12px', border: '1px solid var(--border)', borderRadius: 'var(--r-md)', background: 'var(--surface-sunken)' }}>
            <TagChip name={draft.name.trim() || t('adm.tags.previewName')} color={draft.color || null} />
            <span className="t-sm muted">{t('adm.tags.previewHint')}</span>
          </div>
        </div>
        <Notice text={error} />
      </form>
    </EditLayer>
  )
}
